import React from 'react';
import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation';
import type { ControlProps } from '@/app/components/search/components/Form';
import { getTARatingNumber } from '@/app/common/filters/surfaces/labels/RatingLabel';
import { useHasTripAdvisorHotels } from '@/app/common/reviews/useHasTripAdvisorHotels.ts';
import { TA_RATING_FILTER_TRANSLATION_KEYS } from '@/app/common/reviews/taRatingFilterKeys.ts';
import { OPTIONS_SORT, getMultilistboxValue } from '../../utils';
import getListboxContainer from '../getListboxContainer';
import { RatingStyled } from './RatingContainer';
import FilterContainer from '../../FilterContainer';
import type { CommonProps } from '../CommonProps';
import type { AccommodationRating } from '../../../../../utils/api/filtersAPIResponseTypes';

enum RATING_VALUE {
  EXCELLENT = 'excellent',
  VERY_GOOD = 'verygood',
  GOOD = 'good',
  PLEASANT = 'pleasant',
  POOR = 'poor',
}

const getRatingNumber = (rating: RATING_VALUE) => {
  switch (rating) {
    case RATING_VALUE.EXCELLENT:
      return '8.7+';
    case RATING_VALUE.VERY_GOOD:
      return '8.1+';
    case RATING_VALUE.GOOD:
      return '7.5+';
    case RATING_VALUE.PLEASANT:
      return '6.0+';
    default:
      return '≤6.0';
  }
};

// Filters are page-level (no per-hotel scale): any TripAdvisor hotel in the result list forces
// the TA thresholds and the TA-taxonomy wording, otherwise the TrustYou coloured bands show.
const RatingOptionLabel = ({ value, text, taText }: { value: RATING_VALUE; text: string; taText: string }) => {
  const isTripAdvisor = useHasTripAdvisorHotels();

  return isTripAdvisor ? (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontWeight: 700 }}>{getTARatingNumber(value)}</span>
      {taText}
    </span>
  ) : (
    <>
      <div>{getRatingNumber(value)}</div>
      {text}
    </>
  );
};

const getRatingOption = ({
  value,
  text,
  taText,
  isWaiting,
  accommodationRating,
}: {
  value: RATING_VALUE;
  text: string;
  taText: string;
  isWaiting: boolean;
  accommodationRating?: AccommodationRating;
}) => ({
  value,
  label: (
    <RatingStyled className={`filter-rating-label-${value}`}>
      <RatingOptionLabel value={value} text={text} taText={taText} />
    </RatingStyled>
  ),
  disabled: isWaiting || (accommodationRating && !accommodationRating[value]),
  ...(accommodationRating ? { addLabel: accommodationRating[value] ? `${accommodationRating[value]}` : '0' } : {}),
});

const getRatingOptions = ({
  t,
  isWaiting = false,
  accommodationRating,
}: {
  t: TranslationFunction;
  isWaiting: boolean;
  accommodationRating?: AccommodationRating;
}) => [
  getRatingOption({
    value: RATING_VALUE.EXCELLENT,
    text: t('vacation.oss.filters.rating.excellent'),
    taText: t(TA_RATING_FILTER_TRANSLATION_KEYS[RATING_VALUE.EXCELLENT]),
    isWaiting,
    accommodationRating,
  }),
  getRatingOption({
    value: RATING_VALUE.VERY_GOOD,
    text: t('vacation.oss.filters.rating.verygood'),
    taText: t(TA_RATING_FILTER_TRANSLATION_KEYS[RATING_VALUE.VERY_GOOD]),
    isWaiting,
    accommodationRating,
  }),
  getRatingOption({
    value: RATING_VALUE.GOOD,
    text: t('vacation.oss.filters.rating.good'),
    taText: t(TA_RATING_FILTER_TRANSLATION_KEYS[RATING_VALUE.GOOD]),
    isWaiting,
    accommodationRating,
  }),
  getRatingOption({
    value: RATING_VALUE.PLEASANT,
    text: t('vacation.oss.filters.rating.fair'),
    taText: t(TA_RATING_FILTER_TRANSLATION_KEYS[RATING_VALUE.PLEASANT]),
    isWaiting,
    accommodationRating,
  }),

  // TODO: uncomment to filter by rating poor
  // getRatingOption({
  //   value: RATING_VALUE.POOR,
  //   text: t(''vacation.oss.filters.rating.poor'),
  //   isWaiting,
  //   accommodationRating,
  // }),
];

type GetRatingField = {
  searchParams: SearchParams;
  t: TranslationFunction;
  commonProps: CommonProps;
};

const getRatingField = ({ t, searchParams, commonProps }: GetRatingField): ControlProps => {
  const { dynamicFiltersResponse, isWaiting } = commonProps;
  const accommodationRating = dynamicFiltersResponse ? dynamicFiltersResponse.accommodationRating : undefined;
  const value = getMultilistboxValue(searchParams[URLParameters.RATING], false);

  const controls = getListboxContainer({
    fieldName: URLParameters.RATING,
    containerName: FilterContainer.RatingContainer,
    containerTitle: t('vacation.oss.components.searchfilters.group.rating'),
    options: getRatingOptions({
      t,
      isWaiting,
      accommodationRating,
    }),
    value,
    fieldClassName: '',
    optionsSort: OPTIONS_SORT.ALPHABET,
    ...commonProps,
  });

  return {
    ...controls,
  };
};

export default getRatingField;
